import styled from "styled-components";
import { FC } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import Text from "@/components/Text";
import ExportButton from "@/components/ExportButton";
import Button from "@/components/Button";
import { api } from "@/utils/api";
import { selectTeam } from "@/selectors";
import { RootState } from "@/utils/store";

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 24px;
  margin-top: 48px;
`;

const Card = styled.div`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.grayscale};
  padding: 24px;
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.h4`
  display: flex;
  align-items: center;
  margin: 0 0 12px;
  > svg {
    margin-right: 8px;
  }
`;

const Li = styled(Text).attrs({ as: "li", $type: "smallMedium" })`
  margin: 0 0 16px;
  padding-left: 26px;
  position: relative;
  font-weight: 400;
  &:last-child {
    margin: 0;
  }
  &::before {
    position: absolute;
    content: "";
    top: 5px;
    left: 10px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.purpleLight};
  }
`;

const CardFooter = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  padding-top: 24px;
  gap: 12px;
`;

interface Props {
  name?: string;
}

const OptimisationExport: FC<Props> = ({ name }) => {
  const { id, teamId } = useParams();

  const faq = useSelector(
    (rootState: RootState) =>
      selectTeam(rootState, teamId!)?.preferences?.youtube_testing_faq
  );

  const fetchCsv = () =>
    api.get<never, string>(`/v1/optimisation-tests/${id}/ga-csv`);

  return (
    <Grid>
      <Card>
        <CardHeader>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="12" fill="#4136CF" fillOpacity="0.08" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.12109 7C8.56881 7 8.12109 7.44772 8.12109 8C8.12109 8.55228 8.56881 9 9.12109 9H13.4777L7.2868 15.299C6.89967 15.6929 6.90515 16.3261 7.29904 16.7132C7.69293 17.1003 8.32607 17.0948 8.7132 16.701L15.0009 10.3034V15C15.0009 15.5523 15.4487 16 16.0009 16C16.5532 16 17.0009 15.5523 17.0009 15V8C17.0009 7.44772 16.5532 7 16.0009 7H15.8627H9.12109Z"
              fill="#4136CF"
            />
          </svg>
          <Text $type="captionSemibold" as="span">
            Export CSV for Google Ads
          </Text>
        </CardHeader>
        <ul>
          <Li>
            After seting up tests, export CSV file to add it to Google Ads
            platform and make ir ready to run project.
          </Li>
        </ul>
        <CardFooter>
          <ExportButton
            fetchCsv={fetchCsv}
            fileName={`${name}-optimisation-test.csv`}
          />
          <Button
            $type="secondary"
            small
            as="a"
            href="https://ads.google.com/home/tools/ads-editor/"
            target="_blank"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.23047 1.84645L1.23047 14.1541C1.23047 14.494 1.50599 14.7695 1.84585 14.7695H14.1535C14.4934 14.7695 14.7689 14.494 14.7689 14.1541V1.84645C14.7689 1.50659 14.4934 1.23107 14.1535 1.23107H1.84585C1.50599 1.23107 1.23047 1.50659 1.23047 1.84645Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.46094 5.84766H10.1532V9.53996"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.8457 10.1553L10.1534 5.84766"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Google Ads Editor
          </Button>
        </CardFooter>
      </Card>
      {!!faq?.length && (
        <Card>
          <CardHeader>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="12"
                fill="#4136CF"
                fillOpacity="0.08"
              />
              <path
                d="M10.8256 14.5702C10.8256 14.2255 10.8591 13.8933 10.926 13.5736C10.9982 13.2489 11.1141 12.9491 11.2737 12.6744C11.4128 12.4096 11.5673 12.1998 11.7373 12.045C11.9073 11.8851 12.0979 11.7277 12.3091 11.5729C12.5202 11.413 12.7546 11.2007 13.0121 10.936C13.2233 10.7112 13.3624 10.4889 13.4294 10.2691C13.4963 10.0443 13.5298 9.8045 13.5298 9.54973C13.5298 9.38488 13.5118 9.23002 13.4757 9.08515C13.4397 8.94028 13.3753 8.81539 13.2826 8.71049C13.1383 8.52566 12.9529 8.39578 12.7263 8.32084C12.4996 8.24092 12.2575 8.20095 12 8.20095C11.7888 8.20095 11.5854 8.22843 11.3896 8.28338C11.199 8.33333 11.0368 8.41326 10.9029 8.52316C10.7483 8.64305 10.635 8.79042 10.5629 8.96526C10.4908 9.1401 10.4573 9.33242 10.4625 9.54223H8.5C8.55151 9.04269 8.69058 8.58061 8.91722 8.15599C9.14901 7.72638 9.45291 7.3842 9.82892 7.12943C10.1225 6.91962 10.4522 6.76226 10.8179 6.65736C11.1887 6.55245 11.5596 6.5 11.9305 6.5C12.4971 6.5 13.0302 6.58492 13.5298 6.75477C14.0346 6.92461 14.4544 7.20436 14.7892 7.59401C15.021 7.84877 15.1961 8.14351 15.3146 8.4782C15.4382 8.8129 15.5 9.15259 15.5 9.49728C15.5 10.0118 15.3841 10.4814 15.1523 10.906C14.9257 11.3306 14.6372 11.7228 14.287 12.0824C14.1221 12.2523 13.965 12.4021 13.8157 12.532C13.6663 12.6619 13.5272 12.7893 13.3985 12.9142C13.2748 13.0341 13.1718 13.1614 13.0894 13.2963C12.9452 13.5311 12.8602 13.7309 12.8344 13.8958C12.8087 14.0556 12.7958 14.2804 12.7958 14.5702H10.8256ZM10.8411 17.5V15.5143H12.7958V17.5H10.8411Z"
                fill="#4136CF"
              />
            </svg>
            <Text $type="captionSemibold" as="span">
              FAQ
            </Text>
          </CardHeader>
          <ul>
            {faq?.map((row, index) => (
              <Li key={index}>{row.text}</Li>
            ))}
          </ul>
        </Card>
      )}
    </Grid>
  );
};

export default OptimisationExport;
